import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [activeCategory, setActiveCategory] = useState(categoryId || 'japanese');

  useEffect(() => {
    if (categoryId) {
      setActiveCategory(categoryId);
    }
  }, [categoryId]);

  const currentCategory = categories.find((c) => c.id === activeCategory) || categories[0];
  const categoryArticles = getArticlesByCategory(activeCategory);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - h漫导航`,
    description: currentCategory.description,
    url: `https://hmanga.example.com/category/${activeCategory}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://hmanga.example.com/article/${article.slug}`,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1">
          {/* Header */}
          <section className="bg-background-100 py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '分类浏览' },
                  { label: currentCategory.name },
                ]}
              />
              <div className="mt-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${currentCategory.icon} text-lg`}></i>
                  </div>
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
                    {currentCategory.name}
                  </h1>
                </div>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
                  {currentCategory.description}
                </p>
              </div>
            </div>
          </section>

          {/* Category Tabs */}
          <section className="border-b border-background-200/70 bg-background-50 sticky top-16 z-40">
            <div className="max-w-7xl mx-auto px-4 md:px-6 overflow-x-auto">
              <div className="flex items-center gap-1 py-3 min-w-max">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-500 hover:text-foreground-900 hover:bg-background-100'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Articles */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:shadow-md transition-all cursor-pointer block"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {article.featured && (
                        <div className="absolute top-3 right-3">
                          <span className="px-2 py-0.5 rounded-md bg-accent-500 text-white text-xs font-medium whitespace-nowrap">
                            精选
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h2 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2 leading-snug">
                        {article.title}
                      </h2>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 rounded-md bg-background-100 text-xs text-foreground-500 whitespace-nowrap"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime} 分钟
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.views.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-100 mx-auto mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无内容，请浏览其他分类</p>
              </div>
            )}
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}